/**
 * The KnowIt adapter for Workspace Knowledge (`knowledge.status|refresh|refresh.cancel|activity`,
 * bare JSON — see `knowledgeTypes.ts`). Reads throw on any failure, so an outage never renders as
 * a sound workspace; `refresh` turns the contract's refusals (409/503/403) into typed results.
 */
import { ApiError } from "~/api/errors";
import {
  type KnowledgeApi,
  type KnowledgeEstimate,
  type KnowledgeRefreshResult,
  type KnowledgeRun,
  KnowledgeRunningSchema,
  KnowledgeRunSchema,
  KnowledgeStatusSchema,
  toRunState,
} from "~/api/knowledgeTypes";

export interface KnowledgeTransport {
  /** A GET that throws the estate's typed error on any non-2xx. */
  get(path: string): Promise<unknown>;
  /** A POST that hands back the status and the body, whatever the status (401 still refreshes). */
  postStatus(
    path: string,
    body: Record<string, unknown>,
  ): Promise<{ status: number; body: unknown }>;
}

type Json = Record<string, unknown>;

function obj(value: unknown): Json {
  return value && typeof value === "object" && !Array.isArray(value) ? (value as Json) : {};
}

function text(value: unknown): string | null {
  return typeof value === "string" && value.trim().length > 0 ? value : null;
}

/** The refusal's own words: `sentence`, then `message`, then the bare `error` text. */
function sentenceOf(body: Json): string | null {
  return text(body.sentence) ?? text(body.message) ?? text(body.error);
}

function estimateOf(value: unknown): KnowledgeEstimate {
  return Object.fromEntries(
    Object.entries(obj(value)).filter(
      (entry): entry is [string, number | null] =>
        typeof entry[1] === "number" || entry[1] === null,
    ),
  );
}

export const KNOWLEDGE_ACTIVITY_DEFAULT_LIMIT = 20;

export function createKnowItKnowledge(transport: KnowledgeTransport): KnowledgeApi {
  return {
    status: async () => KnowledgeStatusSchema.parse(await transport.get("knowledge.status")),

    refresh: async (stage, force) => {
      const { status, body } = await transport.postStatus("knowledge.refresh", {
        stage,
        ...(force ? { force: true } : {}),
      });
      const payload = obj(body);
      if (status >= 200 && status < 300) {
        return {
          kind: "started",
          runId: String(payload.runId ?? ""),
          stage: String(payload.stage ?? stage),
          estimate: estimateOf(payload.estimate),
        } satisfies KnowledgeRefreshResult;
      }
      if (status === 409) {
        const running = KnowledgeRunningSchema.safeParse(payload.running);
        return {
          kind: "already_running",
          running: running.success ? running.data : null,
          sentence: text(payload.sentence),
        };
      }
      if (status === 503) {
        return {
          kind: "refused",
          error: text(payload.error) ?? "unavailable",
          sentence: sentenceOf(payload) ?? "The server refused to start this refresh.",
        };
      }
      if (status === 403) {
        return { kind: "forbidden", sentence: text(payload.sentence) ?? text(payload.message) };
      }
      throw new ApiError(
        sentenceOf(payload) ?? `The refresh could not be started (${status}).`,
        status,
      );
    },

    cancel: async (runId) => {
      const { status, body } = await transport.postStatus("knowledge.refresh.cancel", { runId });
      if (status >= 200 && status < 300) {
        return toRunState(obj(body).status ?? "cancelled");
      }
      throw new ApiError(
        sentenceOf(obj(body)) ?? `The refresh could not be stopped (${status}).`,
        status,
      );
    },

    activity: async (limit = KNOWLEDGE_ACTIVITY_DEFAULT_LIMIT) => {
      const raw = await transport.get(`knowledge.activity?limit=${encodeURIComponent(limit)}`);
      const list = Array.isArray(raw) ? raw : obj(raw).runs;
      return (Array.isArray(list) ? list : []).map(
        (run): KnowledgeRun => KnowledgeRunSchema.parse(run),
      );
    },
  };
}
